import type { URLParams, SurveyResponse } from '../../types';
import {
  SurveyApiService,
  SurveyEligibilityService,
  SurveyStateManager
} from './index';
import {
  ErrorService,
  DeviceService
} from '../core';
import { UIRendererService } from '../ui';
import { IpAddressService, type IpInfo, RateLimitService, type RateLimitInfo } from '../security';
import { Logger } from '../../utils';

/**
 * Main controller for survey operations
 * Handles survey initialization, eligibility checking, rate limiting, and submission
 */
export class SurveyController {
  private stateManager: SurveyStateManager;
  private urlParams: URLParams;
  private appElement: HTMLDivElement;
  private ipInfo: IpInfo | null = null;
  private rateLimitInfo: RateLimitInfo | null = null;
  private fullSurveyData: any = null;

  constructor(urlParams: URLParams, appElement: HTMLDivElement) {
    this.stateManager = new SurveyStateManager();
    this.urlParams = urlParams;
    this.appElement = appElement;
  }

  getStateManager(): SurveyStateManager {
    return this.stateManager;
  }

  getUserId(): string {
    if (this.urlParams.anonymous) {
      // For anonymous surveys, prefer userId if provided, otherwise use deviceId
      return this.urlParams.userId || DeviceService.getDeviceId();
    } else {
      // For non-anonymous surveys, use userId if provided, otherwise 'anonymous'
      return this.urlParams.userId || 'anonymous';
    }
  }

  getSurveyId(): string {
    return this.urlParams.surveyId || 'unknown';
  }

  async initializeSurvey(): Promise<void> {
    this.appElement.innerHTML = UIRendererService.renderLoadingState();

    try {
      // First, get the user's IP address and check rate limits
      await this.initializeRateLimiting();

      // If rate limited, show appropriate message
      if (this.rateLimitInfo?.isBlocked) {
        const nextAllowedTime = this.rateLimitInfo.nextAllowedTime;
        const timeUntilAllowed = nextAllowedTime ? new Date(nextAllowedTime).toLocaleString() : 'later';
        const message = `You have exceeded the maximum number of survey submissions. Please try again ${timeUntilAllowed}.`;
        this.appElement.innerHTML = UIRendererService.renderErrorState(message, 429);
        return;
      }

      const userId = this.getUserId();
      const surveyId = this.getSurveyId();

      const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.DEV;
      const apiBaseUrl = isDevelopment
        ? import.meta.env.VITE_API_BASE_URL_DEV || 'http://localhost:8080'
        : import.meta.env.VITE_API_BASE_URL_PROD || 'https://survey-api.askbilly.ai';

      Logger.log(`🔧 Environment: ${isDevelopment ? 'Development' : 'Production'}`);
      Logger.log(`🔧 API Base URL: ${apiBaseUrl}`);
      Logger.log(`🛡️ Rate limit status:`, {
        isBlocked: this.rateLimitInfo?.isBlocked,
        remainingAttempts: this.rateLimitInfo?.remainingAttempts,
        ip: this.ipInfo?.ip
      });

      const eligibilityResult = await SurveyEligibilityService.checkEligibility(userId, surveyId, this.urlParams.anonymous);

      Logger.log('🔍 Eligibility result:', eligibilityResult);
      Logger.log('🔍 Eligible:', eligibilityResult.eligible);
      Logger.log('🔍 Has survey:', !!eligibilityResult.survey);

      if (eligibilityResult.eligible && eligibilityResult.survey) {
        Logger.log('✅ Using survey data:', eligibilityResult.survey);
        this.fullSurveyData = eligibilityResult.survey;
        this.stateManager.setCurrentSurvey(eligibilityResult.survey.questions);
        this.stateManager.setSurveyMetadata({
          name: eligibilityResult.survey.name,
          description: eligibilityResult.survey.description
        });
        Logger.log('📋 Current survey set to:', this.stateManager.getCurrentSurvey());
        Logger.log('📋 Survey metadata:', this.stateManager.getSurveyMetadata());
      } else {
        const reason = eligibilityResult.message || 'You are not eligible to participate in this survey.';
        this.appElement.innerHTML = UIRendererService.renderErrorState(reason, 403);
        return;
      }
    } catch (error) {
      Logger.error('Failed to initialize survey:', error);
      ErrorService.handleError(error, this.appElement);
      throw error; // Re-throw so main.ts knows initialization failed
    }
  }

  async submitSurvey(): Promise<void> {
    try {
      // Pre-submission rate limit check
      await this.initializeRateLimiting();

      if (this.rateLimitInfo?.isBlocked) {
        const nextAllowedTime = this.rateLimitInfo.nextAllowedTime;
        const timeUntilAllowed = nextAllowedTime ? new Date(nextAllowedTime).toLocaleString() : 'later';
        const error = new Error(`Submission blocked due to rate limiting. Please try again ${timeUntilAllowed}.`);
        (error as any).rateLimited = true;
        throw error;
      }

      const userId = this.getUserId();
      const surveyId = this.getSurveyId();
      const deviceId = DeviceService.getDeviceId();

      // Build rate limit info for backend
      const rateLimitInfo = this.ipInfo ? {
        ip: this.ipInfo.ip,
        device_id: deviceId,
        submission_count: this.rateLimitInfo?.submissions.filter(s => s.success).length || 0,
        first_submission_time: this.getFirstSubmissionTime()
      } : undefined;

      const submissionPayload = SurveyApiService.buildSubmissionPayload(
        userId,
        surveyId,
        this.stateManager.getAnswers(),
        deviceId,
        this.urlParams.anonymous,
        this.ipInfo?.ip,
        this.urlParams.emailAddress,
        this.urlParams.source,
        rateLimitInfo
      );

      // Record the submission attempt (before API call)
      if (this.ipInfo) {
        RateLimitService.recordSubmissionAttempt(
          this.ipInfo,
          deviceId,
          surveyId,
          false // Will update to true if successful
        );
      }

      await SurveyApiService.submitSurveyResponse(submissionPayload);

      // Record successful submission
      if (this.ipInfo) {
        RateLimitService.recordSubmissionAttempt(
          this.ipInfo,
          deviceId,
          surveyId,
          true
        );
      }

      return Promise.resolve();
    } catch (error) {
      Logger.error('Survey submission failed:', error);
      return Promise.reject(error);
    }
  }

  buildSurveyResponse(): SurveyResponse {
    const userId = this.getUserId();
    return {
      surveyId: this.urlParams.surveyId || (this.stateManager.getCurrentSurvey()?.id || 'unknown'),
      userId: userId,
      projectCode: this.urlParams.referralCode || 'default',
      answers: this.stateManager.getAnswers()
    };
  }

  notifyAndroidApp(): void {
    if (window.AndroidInterface && typeof window.AndroidInterface.onSurveyCompleted === 'function') {
      const userId = this.getUserId();
      window.AndroidInterface.onSurveyCompleted(
        JSON.stringify({
          userId: userId,
          surveyId: this.urlParams.surveyId || 'unknown',
          status: 'COMPLETED'
        })
      );
    }
  }

  private async initializeRateLimiting(): Promise<void> {
    try {
      // Get IP address
      if (!this.ipInfo) {
        this.ipInfo = await IpAddressService.getUserIP();
      }

      // Check rate limits
      const deviceId = DeviceService.getDeviceId();
      const surveyId = this.getSurveyId();

      this.rateLimitInfo = RateLimitService.checkRateLimit(
        this.ipInfo,
        deviceId,
        surveyId
      );

      Logger.log('🛡️ Rate limiting initialized:', {
        ip: this.ipInfo.ip,
        isBlocked: this.rateLimitInfo.isBlocked,
        remainingAttempts: this.rateLimitInfo.remainingAttempts
      });
    } catch (error) {
      Logger.log('⚠️ Failed to initialize rate limiting:', error);
      // Continue without rate limiting if it fails
      this.ipInfo = { ip: 'unknown', timestamp: Date.now(), source: 'api' };
      this.rateLimitInfo = {
        submissions: [],
        isBlocked: false,
        remainingAttempts: 3
      };
    }
  }

  private getFirstSubmissionTime(): string | undefined {
    if (!this.rateLimitInfo?.submissions.length) return undefined;

    const firstSubmission = this.rateLimitInfo.submissions
      .filter(s => s.success)
      .sort((a, b) => a.timestamp - b.timestamp)[0];

    return firstSubmission ? new Date(firstSubmission.timestamp).toISOString() : undefined;
  }

  getRateLimitInfo(): RateLimitInfo | null {
    return this.rateLimitInfo;
  }

  getIpInfo(): IpInfo | null {
    return this.ipInfo;
  }

  getFullSurveyData(): any {
    return this.fullSurveyData;
  }
}